import { useCallback, useState } from "react";
import { DetailRow } from "./DetailRow";
import { ReceiptImageViewer } from "./ReceiptImageViewer";
import { SourceBadge } from "./SourceBadge";
import { StatusBadge } from "./StatusBadge";
import {
  confidenceLabel,
  formatDate,
  formatDateTime,
  formatMoney,
} from "../lib/format";
import type { ExtractedReceiptData, Receipt, Transaction } from "../types";

/** Build extraction DTO from persisted receipt columns when rematch payload is absent. */
export function extractionFromReceipt(receipt: Receipt): ExtractedReceiptData {
  return {
    merchant: receipt.extracted_merchant,
    amount: receipt.extracted_amount,
    currency: receipt.extracted_currency || "EUR",
    date: receipt.extracted_date,
    time: receipt.extracted_time ?? undefined,
    vat: receipt.extracted_vat,
    reference: receipt.extracted_reference ?? undefined,
    payment_method: receipt.extracted_payment_hint ?? undefined,
    line_items: [],
    confidence:
      receipt.ai_confidence != null && receipt.ai_confidence !== ""
        ? Number(receipt.ai_confidence)
        : 0,
    raw: {},
  };
}

export function TransactionDetailsPanel({
  txn,
  confidence,
  reasons,
}: {
  txn: Transaction;
  confidence?: number | null;
  reasons?: string[];
}) {
  const conf =
    confidence != null && !Number.isNaN(confidence)
      ? confidence
      : txn.match_confidence != null && txn.match_confidence !== ""
        ? Number(txn.match_confidence)
        : null;
  const hasFx =
    txn.original_amount != null &&
    txn.original_currency != null &&
    (txn.original_currency !== txn.currency ||
      txn.original_amount !== txn.amount);

  return (
    <div className="panel stack match-side">
      <div className="row between" style={{ alignItems: "flex-start" }}>
        <div>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            Card / bank transaction
          </div>
          <h3
            style={{
              margin: "0.15rem 0 0",
              fontSize: "1.2rem",
              overflowWrap: "anywhere",
              lineHeight: 1.25,
            }}
          >
            {txn.merchant_name}
          </h3>
        </div>
        <div className="row" style={{ gap: "0.35rem", flexShrink: 0 }}>
          <SourceBadge source={txn.import_source} />
          <StatusBadge status={txn.status} />
        </div>
      </div>

      <div className="row between">
        <div>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            Settlement
          </div>
          <div className="amount" style={{ fontSize: "1.45rem" }}>
            {formatMoney(txn.amount, txn.currency)}
          </div>
          {hasFx && (
            <div className="muted" style={{ fontSize: "0.8rem" }}>
              Original {formatMoney(txn.original_amount!, txn.original_currency!)}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            Date
          </div>
          <div>{formatDate(txn.transaction_date)}</div>
          {txn.booking_date && (
            <div className="muted" style={{ fontSize: "0.75rem" }}>
              Booked {formatDate(txn.booking_date)}
            </div>
          )}
        </div>
      </div>

      {conf != null && !Number.isNaN(conf) && (
        <div>
          <div className="row between" style={{ marginBottom: "0.35rem" }}>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Match confidence
            </span>
            <span className="badge badge-suggested">
              {confidenceLabel(conf)} {Math.round(conf * 100)}%
            </span>
          </div>
          <div className="progress-bar">
            <span style={{ width: `${Math.round(conf * 100)}%` }} />
          </div>
          {reasons && reasons.length > 0 && (
            <div className="muted" style={{ marginTop: "0.4rem", fontSize: "0.8rem" }}>
              {reasons.join(" · ")}
            </div>
          )}
        </div>
      )}

      <h4 style={{ fontSize: "0.95rem", margin: "0.25rem 0 0" }}>All details</h4>
      <dl className="detail-list">
        <DetailRow label="Internal ID" value={txn.id} mono />
        <DetailRow label="External ID" value={txn.external_id} mono />
        <DetailRow label="Status" value={txn.status} />
        <DetailRow label="Merchant" value={txn.merchant_name} />
        <DetailRow label="Category" value={txn.merchant_category} />
        <DetailRow label="Description" value={txn.description} />
        <DetailRow
          label="Settlement amount"
          value={formatMoney(txn.amount, txn.currency)}
        />
        <DetailRow label="Settlement currency" value={txn.currency} mono />
        <DetailRow
          label="Original amount"
          value={
            txn.original_amount != null
              ? formatMoney(
                  txn.original_amount,
                  txn.original_currency || txn.currency,
                )
              : null
          }
        />
        <DetailRow label="Original currency" value={txn.original_currency} mono />
        <DetailRow label="Exchange rate" value={txn.exchange_rate} mono />
        <DetailRow
          label="Transaction date"
          value={formatDate(txn.transaction_date)}
        />
        <DetailRow
          label="Booking date"
          value={txn.booking_date ? formatDate(txn.booking_date) : null}
        />
        <DetailRow
          label="Card"
          value={txn.card_last4 ? `••${txn.card_last4}` : null}
          mono
        />
        <DetailRow label="Cardholder" value={txn.cardholder_name} />
        <DetailRow label="Cardholder ID" value={txn.cardholder_id} mono />
        <DetailRow
          label="Match confidence"
          value={
            conf != null && !Number.isNaN(conf)
              ? `${confidenceLabel(conf)} (${(conf * 100).toFixed(1)}%)`
              : null
          }
        />
        <DetailRow label="Match notes" value={txn.match_notes} />
        <DetailRow label="Matched receipt ID" value={txn.matched_receipt_id} mono />
        <DetailRow
          label="Matched at"
          value={txn.matched_at ? formatDateTime(txn.matched_at) : null}
        />
        <DetailRow label="Matched by user ID" value={txn.matched_by_id} mono />
        <DetailRow
          label="Flagged insufficient"
          value={
            txn.insufficient_flagged_at
              ? formatDateTime(txn.insufficient_flagged_at)
              : null
          }
        />
        <DetailRow label="Insufficient: admin note" value={txn.insufficient_reason} />
        <DetailRow
          label="Insufficient: explanation"
          value={txn.insufficient_explanation}
        />
        <DetailRow label="Import batch ID" value={txn.import_batch_id} mono />
        <DetailRow label="Statement / period" value={txn.import_period_label} />
        <DetailRow label="Import file" value={txn.import_filename} />
        <DetailRow
          label="Import source"
          value={
            txn.import_source === "boekhoudgemak"
              ? "Boekhoudgemak (bank)"
              : txn.import_source === "ing_mastercard"
                ? "ING credit card CSV"
                : txn.import_source
          }
        />
        <DetailRow
          label="Created"
          value={txn.created_at ? formatDateTime(txn.created_at) : null}
        />
        <DetailRow
          label="Updated"
          value={txn.updated_at ? formatDateTime(txn.updated_at) : null}
        />
      </dl>
    </div>
  );
}

export function ReceiptDetailsPanel({
  receipt,
  extraction,
  previewUrl,
  fileUrl,
  isPdf,
}: {
  receipt: Receipt;
  extraction: ExtractedReceiptData;
  previewUrl: string | null;
  fileUrl: string | null;
  isPdf: boolean;
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const closeViewer = useCallback(() => setViewerOpen(false), []);
  const amount =
    extraction.amount != null
      ? formatMoney(String(extraction.amount), extraction.currency || "EUR")
      : receipt.extracted_amount != null
        ? formatMoney(
            String(receipt.extracted_amount),
            receipt.extracted_currency || "EUR",
          )
        : null;
  const ref =
    extraction.reference ||
    receipt.extracted_reference ||
    extraction.invoice_number ||
    extraction.order_number ||
    extraction.ticket_number ||
    extraction.authorization_code ||
    extraction.receipt_number;

  return (
    <div className="panel stack match-side">
      <div>
        <div className="muted" style={{ fontSize: "0.75rem" }}>
          Scanned receipt
        </div>
        <h3
          style={{
            margin: "0.15rem 0 0",
            fontSize: "1.2rem",
            overflowWrap: "anywhere",
            lineHeight: 1.25,
          }}
        >
          {extraction.merchant ||
            receipt.extracted_merchant ||
            receipt.original_filename}
        </h3>
      </div>

      <div className="row between">
        <div>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            Extracted amount
          </div>
          <div className="amount" style={{ fontSize: "1.45rem" }}>
            {amount || "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="muted" style={{ fontSize: "0.75rem" }}>
            Receipt date
          </div>
          <div>
            {extraction.date
              ? formatDate(String(extraction.date))
              : receipt.extracted_date
                ? formatDate(String(receipt.extracted_date))
                : "—"}
          </div>
          {(extraction.time || receipt.extracted_time) && (
            <div className="muted" style={{ fontSize: "0.75rem" }}>
              {extraction.time || receipt.extracted_time}
            </div>
          )}
        </div>
      </div>

      <div className="receipt-viewer match-receipt-viewer">
        {!previewUrl ? (
          <p className="muted" style={{ padding: "1.5rem", textAlign: "center" }}>
            Loading scan…
          </p>
        ) : (
          <img src={previewUrl} alt="Receipt" className="receipt-image" />
        )}
      </div>
      {isPdf && previewUrl && (
        <div className="muted" style={{ fontSize: "0.8rem" }}>
          PDF preview (first page)
          {fileUrl && (
            <>
              {" · "}
              <a href={fileUrl} target="_blank" rel="noreferrer">
                Open original PDF
              </a>
            </>
          )}
        </div>
      )}
      {!isPdf && fileUrl && (
        <button
          type="button"
          className="btn btn-ghost btn-block"
          onClick={() => setViewerOpen(true)}
        >
          Open full size
        </button>
      )}
      {viewerOpen && fileUrl && (
        <ReceiptImageViewer
          src={fileUrl}
          downloadName={receipt.original_filename}
          onClose={closeViewer}
        />
      )}

      <h4 style={{ fontSize: "0.95rem", margin: "0.25rem 0 0" }}>All details</h4>
      <dl className="detail-list">
        <DetailRow label="Receipt ID" value={receipt.id} mono />
        <DetailRow label="Filename" value={receipt.original_filename} mono />
        <DetailRow label="Content type" value={receipt.content_type} mono />
        <DetailRow
          label="File size"
          value={
            receipt.file_size
              ? `${(receipt.file_size / 1024).toFixed(1)} KB`
              : null
          }
        />
        <DetailRow
          label="Merchant"
          value={extraction.merchant || receipt.extracted_merchant}
        />
        <DetailRow label="Amount" value={amount} />
        <DetailRow
          label="Currency"
          value={extraction.currency || receipt.extracted_currency}
          mono
        />
        <DetailRow
          label="Date"
          value={
            extraction.date
              ? formatDate(String(extraction.date))
              : receipt.extracted_date
                ? formatDate(String(receipt.extracted_date))
                : null
          }
        />
        <DetailRow
          label="Time"
          value={extraction.time || receipt.extracted_time}
          mono
        />
        <DetailRow
          label="VAT"
          value={
            extraction.vat != null
              ? String(extraction.vat)
              : receipt.extracted_vat != null
                ? String(receipt.extracted_vat)
                : null
          }
        />
        <DetailRow label="Reference" value={ref} mono />
        <DetailRow label="Invoice number" value={extraction.invoice_number} mono />
        <DetailRow label="Order number" value={extraction.order_number} mono />
        <DetailRow label="Ticket number" value={extraction.ticket_number} mono />
        <DetailRow
          label="Authorization"
          value={extraction.authorization_code}
          mono
        />
        <DetailRow label="Receipt number" value={extraction.receipt_number} mono />
        <DetailRow
          label="Payment method"
          value={extraction.payment_method || receipt.extracted_payment_hint}
        />
        <DetailRow
          label="Card on receipt"
          value={extraction.card_last4 ? `••${extraction.card_last4}` : null}
          mono
        />
        <DetailRow label="Address" value={extraction.address} />
        <DetailRow label="AI provider" value={receipt.ai_provider} />
        <DetailRow
          label="AI confidence"
          value={
            extraction.confidence != null
              ? `${(extraction.confidence * 100).toFixed(1)}%`
              : receipt.ai_confidence
          }
        />
        <DetailRow
          label="Suggested transaction"
          value={receipt.suggested_transaction_id}
          mono
        />
        <DetailRow label="Match status" value={receipt.match_status} />
        <DetailRow label="Uploaded by user ID" value={receipt.uploaded_by_id} mono />
        <DetailRow
          label="Uploaded"
          value={receipt.created_at ? formatDateTime(receipt.created_at) : null}
        />
        {extraction.line_items?.length > 0 && (
          <DetailRow
            label="Line items"
            value={
              <pre
                className="mono"
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: "0.75rem",
                }}
              >
                {JSON.stringify(extraction.line_items, null, 2)}
              </pre>
            }
          />
        )}
      </dl>
    </div>
  );
}
